/* eslint-disable @typescript-eslint/indent */
import { getBFFApiClient } from '@acp/feature/auth';
import {
 APIResponse, Notice, OneOffENRequestResponse, TenancyDetail,
} from '@acp/shared';
import HttpStatusCodes from 'http-status-codes';
import { ActiveNoticeFilters } from './types/filters';

async function getUrgentActiveNotices({
    propertyManagerId = undefined,
}: {
    propertyManagerId?: string;
}) {
    const response = await getBFFApiClient().get<APIResponse<{
        total: number;
        items: Notice[];
    }>>(
        '/v1/notice/analytics/urgent-active-notices',
        {
            params: {
                propertyManagerId,
            },
        }
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get urgent access issues: ${response.data.error}`);
    }

    return response.data.data;
}

async function getActiveNotices(
    params: ActiveNoticeFilters,
    pageNumber: number,
    pageSize: number
) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const queryParams: any = {
        query: params.query ?? '',
        facet: params.facets,
        page: pageNumber,
        propertyId: params.propertyId,
        'property-manager': [params.propertyManager],
        'page-size': pageSize,
        resolved: params.resolved,
    };

    const response = await getBFFApiClient().get<APIResponse<{
        total: number;
        items: Notice[];
        unfilteredTotal: number;
    }>>('/v1/notice', {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        params: queryParams,
    });

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get properties: ${response.data.error}`);
    }

    return response.data.data;
}

async function resolveActiveNotice(noticeId: number, propertyId: number, resolveReason: string) {
    const response = await getBFFApiClient().post<APIResponse<{ success: boolean }>>(
        `/v1/notice/${noticeId}/resolve`,
        {
            resolveReason,
            noticeId,
            propertyId,
        }
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to resolve access issue');
    }

    return response.data.data;
}

// TODO: Get rid of this.
async function getPropertyTenancy(propertyId: number) {
    const response = await getBFFApiClient().get<APIResponse<TenancyDetail>>(`/v1/property/${propertyId}/tenancy`);

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get property tenancy');
    }

    return response.data.data;
}

async function requestOneOffEN(propertyId: number, serviceIds: string, noticeId: number) {
    const response = await getBFFApiClient().post<APIResponse<OneOffENRequestResponse>>(
        `/v1/property/${propertyId}/entry-notification/one-off`,
        {
            serviceIds,
            noticeId,
        }
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(response?.data?.error ?? 'Failed to submit once off en request');
    }

    return response.data.data;
}

async function sendNotificationAndResolveActiveNotice(
    noticeId: number,
    propertyId: number,
    toAddresses: string[],
    ccAddresses: string[],
    message?: string | null
) {
    const response = await getBFFApiClient().post<APIResponse<null>>('/v1/notice/send-occupant-notification', {
        noticeId,
        propertyId,
        toAddresses,
        ccAddresses,
        message,
    });

    if (!response.data.success) {
        throw new Error(response.data.error);
    }

    if (response.status !== HttpStatusCodes.OK) {
        throw new Error('Failed to send email');
    }

    return response.data.data;
}

async function sendNotification(
    propertyId: number,
    toAddresses: string[],
    ccAddresses: string[],
    message?: string | null
) {
    const response = await getBFFApiClient().post<APIResponse<null>>('/v1/notice/send-occupant-notification', {
        propertyId,
        toAddresses,
        ccAddresses,
        message,
    });

    if (!response.data.success) {
        throw new Error(response.data.error);
    }

    if (response.status !== HttpStatusCodes.OK) {
        throw new Error('Failed to send occupant notification');
    }

    return response.data.data;
}

export {
    getActiveNotices,
    getUrgentActiveNotices,
    getPropertyTenancy,
    sendNotificationAndResolveActiveNotice,
    sendNotification,
    resolveActiveNotice,
    requestOneOffEN,
};
